import type { AccountContacts } from './types'

/** Seconds the resend button stays disabled. The server sends this; this is the fallback. */
export const DEFAULT_RESEND_COOLDOWN = 60

export const OTP_LENGTH = 6

/**
 * The password rules, stated up front rather than discovered through rejection.
 *
 * They mirror the backend's configured `AUTH_PASSWORD_VALIDATORS` (length, not entirely
 * numeric, not a common password, not similar to your name). The backend is still the
 * authority: it returns field errors, and this list only saves the user a round trip. Keys,
 * not sentences, so they go through i18n like everything else.
 */
export const PASSWORD_RULE_KEYS = [
  'auth.password.rules.length',
  'auth.password.rules.notNumeric',
  'auth.password.rules.notCommon',
  'auth.password.rules.notPersonal',
] as const

/** Where each workspace's dashboard lives. `/app` redirects to the default one. */
export const WORKSPACE_HOME: Record<string, string> = {
  landlord: '/app/landlord',
  affiliate: '/app/affiliate',
  admin: '/app/admin',
  support: '/app/support',
  institution: '/app/institution',
}

/**
 * Each workspace's own profile page (2026-09-27: every workspace has one). The account menu's
 * "My profile" and "Change your password" lead here — the latter with `?tab=security` — and so
 * does the old `/app/settings/password`, so there is one place to manage an account, reached the
 * same way from every workspace.
 */
export const WORKSPACE_PROFILE: Record<string, string> = {
  landlord: '/app/landlord/settings/profile',
  affiliate: '/app/affiliate/profile',
  admin: '/app/admin/profile',
  support: '/app/support/profile',
  institution: '/app/institution/profile',
}

/**
 * A `next` parameter is a redirect target supplied by whoever built the link. Only a
 * **same-origin, path-only** value is ever followed: `//evil.com` and `https://evil.com` are
 * both valid URLs that a naive `router.push` would happily send the user to, taking their
 * freshly minted session with them.
 */
export function safeNext(next: unknown, fallback = '/app'): string {
  if (typeof next !== 'string' || next.length === 0) return fallback
  // Must start with exactly one slash. `//host` and `/\host` are protocol-relative.
  if (!next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return fallback
  // A scheme anywhere before the first slash-delimited segment, or a backslash, is a smuggle.
  if (/^\/[^/]*:/.test(next) || next.includes('\\')) return fallback
  return next
}

/**
 * Where a fresh sign-in lands: `next`, or the workspace's home -- by way of the confirm step
 * while the WhatsApp number is on hold (2026-10-27, D28), so the owner proves it again with a
 * code there and then rather than finding out at their next sign-in. A landlord who must confirm
 * a contact first is sent there by the router guard anyway.
 */
export function afterSignIn(
  state: { defaultWorkspace: string | null; contacts: AccountContacts | null },
  next: unknown,
): string | { path: string; query: Record<string, string> } {
  const home = state.defaultWorkspace ? WORKSPACE_HOME[state.defaultWorkspace] : undefined
  const target = safeNext(next, home ?? '/auth/no-workspace')
  if (state.contacts?.phone_status === 'on_hold' && !state.contacts.must_confirm) {
    return { path: '/auth/confirm', query: { optional: '1', next: target } }
  }
  return target
}
